import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import BackButton from '../common/BackButton';
import './ClubSettings.css';

// 동아리 설정 페이지 컴포넌트
export default function ClubSettings() {
    const { id: clubId } = useParams();
    const navigate = useNavigate();

    // 동아리 정보 저장
    const [club, setClub] = useState(null);

    // 지원 자격 입력값
    const [eligibility, setEligibility] = useState('');

    // 활동 내용 입력값
    const [activities, setActivities] = useState('');

    // 새로 업로드할 이미지 파일
    const [imageFile, setImageFile] = useState(null);

    // 이미지 미리보기 URL
    const [imagePreview, setImagePreview] = useState('');

    // 로딩 상태
    const [loading, setLoading] = useState(true);

    // 페이지 실행 시 동아리 설정 정보 조회
    useEffect(() => {
        fetchClubSetting();
    }, [clubId]);

    // 동아리 설정 정보와 권한 확인
    async function fetchClubSetting() {
        setLoading(true);

        // 현재 로그인 사용자 조회
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
            alert('로그인을 해주세요!');
            navigate('/login');
            return;
        }

        // users 테이블에서 사용자 id 조회
        const { data: userData, error: userError } = await supabase
            .from('users')
            .select('id')
            .eq('email', user.email)
            .maybeSingle();

        if (userError || !userData) {
            alert('사용자 정보를 찾을 수 없습니다.');
            navigate(-1);
            return;
        }

        // 동아리장 권한 확인
        const { data: memberData, error: memberError } = await supabase
            .from('club_members')
            .select('role')
            .eq('club_id', clubId)
            .eq('user_id', userData.id)
            .maybeSingle();

        if (memberError || memberData?.role !== 'leader') {
            alert('동아리장만 설정을 변경할 수 있습니다.');
            navigate(-1);
            return;
        }

        // 동아리 정보 조회
        const { data: clubData, error: clubError } = await supabase
            .from('clubs')
            .select('*')
            .eq('id', clubId)
            .maybeSingle();

        if (clubError || !clubData) {
            alert('동아리 정보를 불러오지 못했습니다.');
            navigate(-1);
            return;
        }

        setClub(clubData);
        setEligibility(clubData.eligibility || '');
        setActivities(clubData.activities || '');
        setImagePreview(clubData.image_url || '');
        setLoading(false);
    }

    // 이미지 선택 시 미리보기 설정
    function handleImageChange(e) {
        const file = e.target.files[0];

        if (!file) return;

        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
    }

    // 동아리 정보 수정
    async function handleUpdate() {
        let imageUrl = club?.image_url || '';

        // 새 이미지가 있으면 Storage에 업로드
        if (imageFile) {
            const fileExt = imageFile.name.split('.').pop();
            const fileName = `club_banner_${Date.now()}.${fileExt}`;
            const filePath = `${clubId}/${fileName}`;

            const { error: uploadError } = await supabase.storage
                .from('club-images')
                .upload(filePath, imageFile);

            if (uploadError) {
                console.log(uploadError);
                alert('이미지 업로드에 실패했습니다.');
                return;
            }

            // 업로드한 이미지의 공개 URL 생성
            const { data } = supabase.storage
                .from('club-images')
                .getPublicUrl(filePath);

            imageUrl = data.publicUrl;
        }

        // clubs 테이블 정보 수정
        const { error } = await supabase
            .from('clubs')
            .update({
                eligibility,
                activities,
                image_url: imageUrl,
            })
            .eq('id', clubId);

        if (error) {
            console.log(error);
            alert('수정에 실패했습니다.');
            return;
        }

        alert('동아리 정보가 수정되었습니다.');
        navigate(`/clubs/${clubId}`);
    }

    // 동아리 삭제
    async function handleDelete() {
        const confirmDelete = window.confirm(
            '정말 동아리를 삭제하시겠습니까? 삭제 후 복구할 수 없습니다.'
        );

        if (!confirmDelete) return;

        // clubs 테이블에서 동아리 삭제
        const { error } = await supabase
            .from('clubs')
            .delete()
            .eq('id', clubId);

        if (error) {
            console.log(error);
            alert('동아리 삭제에 실패했습니다.');
            return;
        }

        alert('동아리가 삭제되었습니다.');
        navigate('/clubs');
    }

    // 로딩 중 화면
    if (loading) {
        return (
            <div className="club-setting-page">
                <BackButton />
                <div className="club-setting-loading">불러오는 중...</div>
            </div>
        );
    }

    return (
        <div className="club-setting-page">
            <BackButton />

            <h1 className="club-setting-title">동아리 설정</h1>

            <div className="club-setting-box">

                {/* 동아리 이미지 설정 */}
                <label className="club-setting-label">동아리 이미지</label>

                <div className="club-setting-image-area">
                    {imagePreview ? (
                        <img
                            src={imagePreview}
                            alt="동아리 이미지"
                            className="club-setting-image"
                        />
                    ) : (
                        <div className="club-setting-placeholder">
                            이미지 없음
                        </div>
                    )}
                </div>

                {/* 이미지 업로드 버튼 */}
                <label className={`image-upload-button ${imageFile ? 'uploaded' : ''}`}>
                    {imageFile ? imageFile.name : '이미지 추가'}
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="club-setting-file"
                    />
                </label>

                <div className="image-guide">
                    ※ 동아리 이미지는 1:1 비율(500 × 500 이상)을 권장합니다.
                </div>

                {/* 지원 자격 수정 */}
                <label className="club-setting-label">지원 자격</label>
                <textarea
                    className="club-setting-textarea"
                    value={eligibility}
                    onChange={(e) => setEligibility(e.target.value)}
                    placeholder="지원 자격을 입력하세요(ex. 자격1/자격2/자격3)"
                />

                {/* 활동 내용 수정 */}
                <label className="club-setting-label">활동 내용</label>
                <textarea
                    className="club-setting-textarea"
                    value={activities}
                    onChange={(e) => setActivities(e.target.value)}
                    placeholder="활동 내용을 입력하세요(ex. 활동1/활동2/활동3)"
                />

                {/* 수정 버튼 */}
                <button className="club-setting-save" onClick={handleUpdate}>
                    수정 완료
                </button>

                {/* 동아리 삭제 버튼 */}
                <button className="club-setting-delete" onClick={handleDelete}>
                    동아리 삭제
                </button>
            </div>
        </div>
    );
}